import { Component, inject, signal, computed, output } from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AssignVendorService } from '../../../services/assign-vendor.service';
import { DynMinMarkupService } from '../../../services/dyn-min-markup.service';
import { DepositService } from '../../../services/deposit.service';
import {
  DynMinMarkupPolicy,
  DynMinMarkupAdjustmentProposal,
  MarkupAdjustmentDecision,
  MarkupEscalationRequest,
} from '../../../models/dyn-min-markup.model';
import {
  CreateCustomerEstimateOptionRequest,
  CreateCustomerEstimateOptionResult,
  CreateCustomerEstimateResponse,
  CustomerEstimateAdminFee,
  CustomerEstimateLineItem,
  ArchivedCustomerEstimateSummary,
  UpdateCustomerEstimateLineItemRequest,
  UpdateCustomerEstimateRequest,
  CUSTOM_CHARGE_TYPE_OPTIONS,
  CustomChargeTypeOption,
  VendorPortionTitle,
} from '../../../models/on-site-estimate.model';

/**
 * Create Customer Estimate (single vendor estimate → customer estimate).
 *
 * Ports the "Creating customer estimate from vendor estimate" flow from the
 * Estimates V2 mockup (context/Estimates V2.html, custEstModal + openCustEst,
 * NOTE 17 / 17a / 17b), wired to the real backend via AssignVendorService:
 *   1. POST /create-customer-estimate   — seed the customer estimate from the vendor estimate
 *   2. PUT  /update-customer-estimate    — persist edits (qty/rate/description, custom lines, admin fee)
 *   3. POST /send-customer-estimate-email — "Save & Send to Customer"
 *
 * Layout is interleaved & sectioned: each customer line renders full width and
 * inline-editable, with its read-only vendor source row directly above it.
 * Below the lines sits the shared "Admin Markup and Added Charge Types" block
 * (admin-fee line + custom added charges) and the GRAND TOTAL.
 *
 * The minimum-markup save gate (Emergency 50% / Non-Emergency 40% of vendor
 * cost, per NOTE 17a) blocks a plain Save below the floor but allows an explicit
 * "Send For Approval" override.
 *
 * MULTI-OPTION MODE (`open({ optionEstimateKeys })`, 2+ open options of one on-site approval): ONE customer
 * option per vendor option, saved together as one multi-option customer estimate. Each option is a tab with its
 * own editable title, lines, markup, admin fee and minimum-markup proposal. The grid always shows the ACTIVE
 * tab: its values live in the same signals the single mode uses (rows, extras, adminOn, ...) and are swapped
 * with the tab's stored copy when the admin switches tabs, so the editing code is shared. One Save (or Save &
 * Send) calls create-customer-estimate-multi-option; the host then runs the deposit and send steps on the MAIN
 * customer estimate key. A saved multi-option estimate is not editable here (create + send only).
 */

/** Local editable row: a live customer line plus its immutable vendor source. */
interface CeRow {
  chargeType: string;
  chargeTypeKey: string;
  description: string;

  vendorRate: number;
  vendorQty: number;
  vendorAmount: number;

  customerRate: number;
  customerQty: number;

  profileMarkupPercent?: number;
  lineType: CustomerEstimateLineItem['lineType'];
  costIncurred: number;

  /** Present once persisted; blank for a brand-new custom charge. */
  customerEstimateDetailKey?: string;
  /**
   * Link back to the source vendor estimate detail (materials/trip detail key or labor key).
   * Echoed back on save so a seeded vendor line re-inserted server-side keeps its vendor linkage —
   * otherwise its vendorRate/qty/amount read back as 0. Absent for admin-added custom charges.
   */
  vendorEstimateDetailKey?: string;
  /** Ad-hoc admin-added charge (no vendor counterpart). */
  isCustomLineItem: boolean;
  /**
   * The vendor estimate this line originated from, for multi-vendor merges (see
   * {@link CreateCustomerEstimateModalComponent.open}'s `vendorEstimateKeys` option). Used to render
   * vendor section headers in the grid. Undefined for single-vendor estimates.
   */
  sourceVendorEstimateKey?: string;
  /**
   * Admin-entered portion title for this line's vendor group (e.g. "Plumbing Portion of Quote"),
   * echoed from the backend's JobSalesOrderToVestimate.PortionTitle. Undefined when no title was
   * set for that vendor — the group header falls back to the plain vendor name.
   */
  sourcePortionTitle?: string;
}

/**
 * One customer option (tab) of a multi-option customer estimate. While a tab is the ACTIVE one its live values are
 * in the component's grid signals; this copy is refreshed whenever the admin switches tabs or saves.
 */
interface CeOptionTab {
  vendorEstimateKey: string;
  /** The vendor option's label: the default title, and the name the admin knows the option by. */
  vendorLabel: string;
  /** The customer-facing title (editable). */
  title: string;
  rows: CeRow[];
  extras: CeRow[];
  adminOn: boolean;
  adminMarkupPercent: number;
  adminMarkupLineItemKey: string | null;
  /** The minimum-markup proposal still waiting for the admin's decision on this option. */
  adjustmentProposal: DynMinMarkupAdjustmentProposal | null;
  /** A proposal the admin accepted on this option; the next save sends it as AcceptProposed. */
  acceptedProposal: DynMinMarkupAdjustmentProposal | null;
  /** The admin chose to fix this option's markup by hand; the next save sends it as ManualOverride. */
  awaitingManualMarkupFix: boolean;
  /** This option's customer estimate key once saved. */
  customerEstimateKey: string | null;
}

/** What a multi-option tab shows: its title, customer total and whether it still needs a markup decision. */
interface CeOptionTabSummary {
  index: number;
  vendorEstimateKey: string;
  title: string;
  vendorLabel: string;
  customerTotal: number;
  needsDecision: boolean;
}

/** The sum of customer rate x qty over rows (the order of operations the grid totals use). */
function rowsSubtotal(rows: CeRow[]): number {
  return rows.reduce((sum, r) => sum + r.customerRate * r.customerQty, 0);
}

/** A group of rows sharing the same source vendor, for the multi-vendor grid's section headers. */
interface VendorRowGroup {
  vendorEstimateKey: string | undefined;
  vendorName: string;
  /** Admin-editable portion title for this vendor's section (e.g. "Plumbing Portion of Quote"). */
  portionTitle: string;
  rows: CeRow[];
  subtotal: number;
}

@Component({
  selector: 'app-create-customer-estimate-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NgTemplateOutlet],
  templateUrl: './create-customer-estimate-modal.component.html',
  styleUrls: ['./create-customer-estimate-modal.component.scss'],
})
export class CreateCustomerEstimateModalComponent {
  private readonly assignVendorSvc = inject(AssignVendorService);
  private readonly dynMinMarkupSvc = inject(DynMinMarkupService);
  private readonly depositSvc = inject(DepositService);

  /** Fired the first time a customer estimate is persisted, so the host can refresh (e.g. flip the button to "View"). */
  readonly customerEstimateCreated = output<{ customerEstimateKey: string; customerTotal: number }>();

  /**
   * Asks the host to open the send-to-customer dialog for a persisted estimate. `isResend` is true
   * for a plain resend, so the host can skip post-creation follow-ups such as the deposit prompt.
   */
  readonly sendRequested = output<{ customerEstimateKey: string; isResend: boolean }>();

  /**
   * Path 2 — "Save As Invoice": asks the host to convert THIS saved customer estimate into a customer
   * invoice. Only meaningful for an already-persisted estimate (edit/view mode); the host opens the
   * Create Customer Invoice modal seeded from this key.
   */
  readonly saveAsInvoiceRequested = output<{ customerEstimateKey: string }>();

  /** The dialog closed (any way: Cancel, X, or the host closing it after a send — never on backdrop click). */
  readonly closed = output<void>();

  // ── Visibility / context ──────────────────────────────────────
  readonly isVisible = signal(false);
  readonly jobKey = signal<string>('');
  readonly vendorEstimateKey = signal<string>('');
  /**
   * Set (2+ entries) when this session is a multi-vendor merge ("Combine Vendor Estimates").
   * Empty for the ordinary single-vendor flow, which continues to use `vendorEstimateKey` alone.
   */
  readonly vendorEstimateKeys = signal<string[]>([]);
  readonly isMultiVendor = computed(() => this.vendorEstimateKeys().length >= 2);
  /** vendorEstimateKey → display name, for grouping the multi-vendor grid by vendor. */
  readonly vendorNamesByKey = signal<Record<string, string>>({});
  readonly customerKey = signal<string>('');
  readonly vendorName = signal<string>('');
  readonly estimateNo = signal<string>('');
  readonly customerEmail = signal<string | null>(null);
  /**
   * Set (2+ entries) when this session is a MULTI-OPTION customer estimate: the vendor's open options chosen in the
   * "Select options to send" step, one customer option each. Empty for the ordinary single flow.
   */
  readonly optionEstimateKeys = signal<string[]>([]);
  /**
   * View mode of a saved customer estimate with options: every option was loaded (GET customer-estimate-options) into
   * the option tabs, shown read-only like a just-saved multi-option estimate.
   */
  readonly savedOptionsView = signal(false);
  readonly isMultiOption = computed(() => this.optionEstimateKeys().length >= 2 || this.savedOptionsView());
  /** vendor option key → label ("Option 1", or the vendor's title): the default customer-facing title. */
  private optionLabels: Record<string, string> = {};
  /** The multi-option tabs (see {@link CeOptionTab}); filled once the preview has loaded. */
  readonly optionTabs = signal<CeOptionTab[]>([]);
  readonly activeOptionIndex = signal(0);
  /** True once the multi-option estimate is saved: it is then shown read-only (editing it here is out of scope). */
  readonly optionsSaved = signal(false);
  /** View mode of a saved multi-option customer estimate: how many options it has (the dialog shows option 1 only). 0 otherwise. */
  readonly viewOptionCount = signal(0);

  /** Emergency job → 50% minimum markup floor; otherwise 40% (NOTE 17a). */
  readonly isEmergency = signal(false);

  /**
   * The customer's dynamic minimum-markup policy, loaded on open from GET /api/DynMinMarkup.
   * Null until loaded (or when no customerKey was supplied); the save gate falls back to the
   * legacy 50/40 defaults in that case so behaviour is safe if the policy can't be fetched.
   */
  readonly minMarkupPolicy = signal<DynMinMarkupPolicy | null>(null);

  /**
   * - 'create' = build a NEW customer estimate from the vendor estimate (editable;
   *   nothing persisted until Save / Save & Send). Allowed even when one already exists.
   * - 'edit'   = load the existing customer estimate and edit it in place (editable).
   * - 'view'   = load the existing customer estimate read-only.
   */
  readonly mode = signal<'create' | 'edit' | 'view'>('create');
  /**
   * Edit mode only: the customer sent this estimate back for changes (a change order). The host passes the customer
   * estimate key it expects; the loaded estimate must be that one, and every save also reports it through
   * `customerEstimateCreated` so the host can run its deposit step before sending (an edit save does not otherwise).
   */
  readonly changeOrder = signal(false);
  /** The customer estimate key the host expects to edit (change order only); '' when none. */
  private expectedCustomerEstimateKey = '';
  readonly isReadOnly = computed(() => this.mode() === 'view' || this.optionsSaved());
  /** Both create and edit render editable inputs and can persist. */
  readonly isEditable = computed(() => !this.isReadOnly());
  /**
   * True once the loaded data reflects a persisted customer estimate (view mode, or a
   * create-mode session whose preview endpoint wasn't available and fell back to the
   * persisting create endpoint). Drives create-vs-update on Save.
   */
  readonly isPersisted = signal(false);

  // ── State ─────────────────────────────────────────────────────
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');

  readonly customerEstimateKey = signal<string | null>(null);
  readonly estimateTitle = signal('');
  readonly rows = signal<CeRow[]>([]);
  readonly extras = signal<CeRow[]>([]);
  /**
   * Admin-edited portion titles for a multi-vendor merge's vendor groups (e.g. "Plumbing Portion of
   * Quote"), keyed by vendorEstimateKey. Seeded from each row's `sourcePortionTitle` on load/create,
   * then edited in place via the per-group title input; sent back as `vendorPortionTitles` on save.
   * Empty for single-vendor estimates.
   */
  readonly portionTitleOverrides = signal<Map<string, string>>(new Map());
  readonly adminOn = signal(true);
  readonly adminMarkupPercent = signal(0);
  readonly adminMarkupLineItemKey = signal<string | null>(null);

  /**
   * Edit mode only: customerEstimateDetailKeys of rows removed from the grid this session.
   * UpdateCustomerEstimateAsync only removes a persisted line when it's explicitly sent back with
   * isDeleted:true — simply omitting a row from the save payload (as create mode does, since
   * nothing is persisted yet to omit FROM) does not delete it server-side. Cleared on open()/save.
   */
  private deletedLineItemKeys: string[] = [];

  // ── History tabs (view/edit) ──────────────────────────────────
  // The immediately-previous, archived estimate (read-only summary), or null when there's no history.
  readonly previousEstimate = signal<ArchivedCustomerEstimateSummary | null>(null);
  /**
   * Create mode only: the prior customer estimate's deposit amount, when that deposit was set but
   * never paid. Creating a new customer estimate replaces the job's live JobSalesInvoice row, and the
   * deposit fields (Isdeposit/DepositAmount) do NOT carry forward onto the replacement — so an unpaid
   * deposit decision on the estimate being superseded would otherwise be silently dropped with no
   * indication to the admin. Populated by a best-effort lookup in loadPreview(); left at 0 (no banner)
   * if the job has no prior estimate, the prior deposit was already paid, or the lookup fails.
   */
  readonly priorUnpaidCustomerDeposit = signal(0);
  readonly showPriorDepositNotice = computed(() => this.priorUnpaidCustomerDeposit() > 0);
  readonly priorDepositNoticeText = computed(
    () =>
      `The current customer estimate for this job has an unpaid deposit of ` +
      `${this.formatCurrency(this.priorUnpaidCustomerDeposit())} set on it. Creating a new customer ` +
      `estimate will replace it, and that deposit will no longer be tracked — set the deposit again ` +
      `on the new estimate if it's still needed.`,
  );
  /**
   * True while THIS estimate's own customer deposit is routed to an SVC manager and still awaiting
   * approval (DepositApprovalFromSVCmanager.IsApproved == null for some recipient). Refreshed by
   * {@link refreshDepositApprovalStatus} whenever applyResponse() loads a persisted estimate — Save &
   * Send / Resend must not fire while this is true, since the deposit hasn't cleared yet.
   */
  readonly customerDepositApprovalPending = signal(false);
  // Which tab is active in view/edit: 'current' (the live estimate) or 'previous' (read-only archive).
  readonly activeTab = signal<'current' | 'previous'>('current');
  // True while the Previous tab is selected — the whole grid is read-only and vendor columns are hidden.
  readonly viewingArchived = computed(() => this.activeTab() === 'previous');

  /**
   * Server-computed propose-then-confirm fix for an under-minimum-markup estimate, from the
   * `adjustmentProposal` field on the preview/create/update response. Non-null means nothing was
   * persisted by the call that produced it — the admin must accept, override, or edit and retry
   * before anything saves. This (not the client-side minMarkupPercent()/activeMinMarkupRow() math,
   * which remain informational-only for the "Minimum Markup Values" popover) is the single source of
   * truth for blocking Save.
   */
  readonly adjustmentProposal = signal<DynMinMarkupAdjustmentProposal | null>(null);
  /** Which Save button (plain save vs. save & mail) triggered the in-flight proposal. */
  readonly pendingDecisionMode = signal<'save' | 'mail' | null>(null);
  /**
   * True once the admin has seen a proposal and chosen to edit manually rather than accept it. The
   * NEXT Save is then sent as ManualOverride with the current grid contents, instead of a fresh
   * (undecided) attempt that would just surface the same proposal again.
   */
  readonly awaitingManualMarkupFix = signal(false);
  /**
   * A proposal the admin accepted. Accepting only applies the numbers to the grid locally (nothing
   * is persisted and no email is sent) — this holds the accepted proposal so the NEXT Save commits
   * it as AcceptProposed rather than as a fresh, undecided attempt.
   */
  readonly acceptedProposal = signal<DynMinMarkupAdjustmentProposal | null>(null);

  /**
   * What the last Trip edit did, shown inline under the grid so the silent adjustment is visible.
   * Cleared on the next edit, on load and on reset. See {@link applyTripShortfallToLabor}.
   */
  readonly tripAdjustmentNote = signal<string>('');

  // Delete-confirmation modal. Holds the line targeted for deletion until the
  // admin confirms; null when the modal is closed.
  readonly pendingDelete = signal<{ index: number; isExtra: boolean } | null>(null);

  // "Add more line item" controls
  readonly customChargeTypeOptions: CustomChargeTypeOption[] = CUSTOM_CHARGE_TYPE_OPTIONS;
  readonly newChargeKey = signal<string>('');
  readonly newRate = signal<number>(0);
  readonly newQty = signal<number>(1);
  readonly newCostIncurred = signal<number>(1); // 1 = Proposed, 0 = Incurred

  /** Minimum-markup popover toggle (template-only local state). */
  showMinPop = false;

  /**
   * Rows bucketed by source vendor, in the order each vendor's group first appears — for the
   * multi-vendor grid's section headers ("Vendor A — N lines"). Only meaningful when
   * {@link isMultiVendor} is true; the template falls back to the flat `rows()` list otherwise.
   */
  readonly groupedRows = computed<VendorRowGroup[]>(() => {
    const names = this.vendorNamesByKey();
    const overrides = this.portionTitleOverrides();
    const groups: VendorRowGroup[] = [];
    const byKey = new Map<string, VendorRowGroup>();

    for (const row of this.rows()) {
      const key = row.sourceVendorEstimateKey ?? '';
      let group = byKey.get(key);
      if (!group) {
        group = {
          vendorEstimateKey: row.sourceVendorEstimateKey,
          vendorName: names[key] || (key ? 'Vendor' : 'Added charges'),
          portionTitle: overrides.get(key) ?? row.sourcePortionTitle ?? '',
          rows: [],
          subtotal: 0,
        };
        byKey.set(key, group);
        groups.push(group);
      }
      group.rows.push(row);
      group.subtotal += row.customerRate * row.customerQty;
    }
    return groups;
  });

  /**
   * The grid is shown per vendor section: a combined estimate being created (2+ vendor estimates), or a saved one
   * whose lines come from 2+ vendor estimates (view / edit), so its sections and titles show as they were sent.
   */
  readonly showVendorGroups = computed(() => {
    if (this.isMultiVendor()) return true;
    if (this.mode() === 'create') return false;
    return new Set(this.rows().map((r) => r.sourceVendorEstimateKey).filter((k) => !!k)).size >= 2;
  });

  /** How many vendor sections the combined estimate has. */
  readonly vendorGroupCount = computed(() => this.groupedRows().filter((g) => !!g.vendorEstimateKey).length);

  /**
   * Set once a save was refused for a missing vendor section title: the empty title inputs are then highlighted.
   * Every vendor's section needs a title the admin types (blank by default, never copied from the vendor).
   */
  readonly portionTitleRequired = signal(false);

  /** The vendor sections still without a title (a combined estimate being created). */
  readonly untitledVendorGroups = computed(() =>
    this.isMultiVendor() ? this.groupedRows().filter((g) => !!g.vendorEstimateKey && !g.portionTitle.trim()) : [],
  );

  /** Admin edits a vendor group's portion title (multi-vendor merge only). */
  setPortionTitle(vendorEstimateKey: string | undefined, title: string): void {
    if (!vendorEstimateKey) return;
    const next = new Map(this.portionTitleOverrides());
    next.set(vendorEstimateKey, title);
    this.portionTitleOverrides.set(next);
  }

  /**
   * Builds the `vendorPortionTitles` save payload from the current groups' titles. Blank titles are
   * still included (as `undefined`/empty) rather than omitted, so clearing a previously-set title
   * actually clears it server-side instead of leaving the old value in place.
   */
  private buildVendorPortionTitles(): VendorPortionTitle[] {
    return this.groupedRows()
      .filter((g) => g.vendorEstimateKey)
      .map((g) => ({
        vendorEstimateKey: g.vendorEstimateKey!,
        portionTitle: g.portionTitle.trim() || undefined,
      }));
  }

  // ── Derived totals ────────────────────────────────────────────
  readonly vendorTotal = computed(() =>
    this.rows().reduce((sum, r) => sum + r.vendorAmount, 0),
  );
  readonly lineSubtotal = computed(() =>
    this.rows().reduce((sum, r) => sum + r.customerRate * r.customerQty, 0),
  );
  readonly extrasSubtotal = computed(() =>
    this.extras().reduce((sum, r) => sum + r.customerRate * r.customerQty, 0),
  );
  readonly adminFee = computed(() =>
    this.adminOn() ? (this.lineSubtotal() + this.extrasSubtotal()) * this.adminMarkupPercent() / 100 : 0,
  );
  readonly grandTotal = computed(() => this.lineSubtotal() + this.extrasSubtotal() + this.adminFee());
  readonly grandMarkupPercent = computed(() => {
    const v = this.vendorTotal();
    return v > 0 ? (this.grandTotal() - v) / v * 100 : 0;
  });
  /**
   * Cost tiers for the minimum-markup popover table, sorted ascending by cost-over-value
   * (lowest first) for display. The backend returns them in save/RowNumber order, which isn't
   * necessarily cost order, so this is a display-only sort — {@link minMarkupPercent} and
   * {@link activeMinMarkupRow} keep their own separate (descending) sorted copy for precedence.
   */
  readonly sortedMinMarkupTiers = computed(() => {
    const policy = this.minMarkupPolicy();
    if (!policy) return [];
    return [...policy.overValuesWithMarkupPercentages].sort(
      (a, b) => a.costOverValue - b.costOverValue,
    );
  });

  /**
   * Required minimum markup %, resolved from the customer's policy against the current vendor
   * total (mirrors the backend precedence, §6.2: highest cost tier the vendor total strictly
   * exceeds wins; else emergency/non-emergency). Falls back to the legacy 50/40 when no policy
   * is loaded, so the gate still works if the API is unreachable.
   */
  readonly minMarkupPercent = computed(() => {
    const policy = this.minMarkupPolicy();
    const fallback = this.isEmergency() ? 50 : 40;
    if (!policy) {
      return fallback;
    }

    const basis = this.vendorTotal();
    const tier = [...policy.overValuesWithMarkupPercentages]
      .filter((t) => t.costOverValue > 0 && t.markupPercentage > 0)
      .sort((a, b) => b.costOverValue - a.costOverValue)
      .find((t) => basis > t.costOverValue);
    if (tier) {
      return tier.markupPercentage;
    }

    const pct = this.isEmergency()
      ? policy.markupPercentageForEmergency
      : policy.markupPercentageForNonEmergency;
    return pct > 0 ? pct : fallback;
  });

  /**
   * Identifies WHICH row of the minimum-markup popover table is currently in effect for this
   * estimate's vendor total, so the popover can highlight it. Mirrors {@link minMarkupPercent}'s
   * precedence exactly (cost tier the vendor total strictly exceeds wins; else emergency/non-emergency)
   * but returns the row identity instead of just the percentage. Null when no policy is loaded (the
   * popover falls back to the static 50/40 table in that case, with nothing to highlight against).
   */
  // ── Below-minimum-markup banner + manager override (V1 parity) ──────────────
  //
  // V1 shows a red alert at the TOP of the create form whenever the estimate is under the customer's minimum
  // markup, and it stays there while the admin edits (CreateNew.cshtml's divForMessageShowingTargetDynamicMinimumMarkup).
  // V2 only had the proposal dialog, which vanishes once dismissed — so the warning and its "request a manager
  // override" route disappeared with it. The banner below is live: it follows the grid as lines are edited.

  /**
   * The estimate's markup is under the customer's required minimum. Computed from the same numbers the footer
   * shows, so it clears the moment the admin edits enough to meet it. The server still has the final say on save.
   */
  readonly belowMinMarkup = computed(() => {
    if (this.isReadOnly() || this.isLoading() || this.vendorTotal() <= 0) return false;
    // A cent of float noise shouldn't trip the banner.
    return this.grandMarkupPercent() < this.minMarkupPercent() - 0.005;
  });

  /**
   * The server's own wording for why this estimate is under the minimum (the proposal's explanation), kept for the
   * banner after the proposal dialog is dismissed. Empty until a preview/save has answered.
   */
  readonly markupExplanation = signal('');

  /** The last proposal the server returned, kept for Send For Approval after the dialog is dismissed. */
  readonly escalationProposal = signal<DynMinMarkupAdjustmentProposal | null>(null);

  readonly escalationOpen = signal(false);
  readonly escalationReason = signal('');
  readonly escalationSending = signal(false);
  readonly escalationError = signal('');
  /** Set once the request is in: the estimate stays unsaved until an approver approves it. */
  readonly escalationSent = signal(false);

  /**
   * Send For Approval is available on the paths the approver can replay: creating a single or combined estimate, and
   * editing a saved one. A multi-option estimate has no replay path server-side, so it is not offered there.
   */
  readonly canEscalate = computed(
    () => !this.isReadOnly() && !this.isMultiOption() && !!this.jobKey() && !!this.customerKey(),
  );

  readonly activeMinMarkupRow = computed<{ kind: 'emergency' | 'nonEmergency' | 'tier'; costOverValue?: number } | null>(() => {
    const policy = this.minMarkupPolicy();
    if (!policy) {
      return null;
    }

    const basis = this.vendorTotal();
    const tier = [...policy.overValuesWithMarkupPercentages]
      .filter((t) => t.costOverValue > 0 && t.markupPercentage > 0)
      .sort((a, b) => b.costOverValue - a.costOverValue)
      .find((t) => basis > t.costOverValue);
    if (tier) {
      return { kind: 'tier', costOverValue: tier.costOverValue };
    }

    return { kind: this.isEmergency() ? 'emergency' : 'nonEmergency' };
  });

  // ── Public API ────────────────────────────────────────────────

  /**
   * Open the modal for the given vendor estimate in one of three modes:
   *
   * - `mode: 'create'` (default) → build a NEW customer estimate: shows a non-persisted
   *   preview (via previewCustomerEstimate, falling back to createCustomerEstimate if that
   *   endpoint isn't available yet). Nothing is written until Save / Save & Send. Allowed
   *   even when a customer estimate already exists — this spawns another one.
   * - `mode: 'edit'` → load the already-existing customer estimate, fully editable, and
   *   persist changes in place on Save.
   * - `mode: 'view'` → load the already-existing customer estimate read-only.
   *
   * `jobKey` and one of `vendorEstimateKey` / `vendorEstimateKeys` are required; the rest tune the
   * header and save-gate copy. Pass `vendorEstimateKeys` (2+ entries, with `vendorNames` for the
   * grid's group headers) for a multi-vendor merge ("Combine Vendor Estimates") — only valid with
   * `mode: 'create'`; edit/view always load a single already-persisted customer estimate by job.
   */
  open(opts: {
    jobKey: string;
    vendorEstimateKey?: string;
    vendorEstimateKeys?: string[];
    vendorNames?: Record<string, string>;
    mode?: 'create' | 'edit' | 'view';
    customerKey?: string;
    vendorName?: string;
    estimateNo?: string;
    customerEmail?: string | null;
    isEmergency?: boolean;
    /**
     * 2+ OPEN options of one on-site approval to send to the customer as ONE multi-option customer estimate (one
     * customer option per vendor option). Create mode only; ignored for a multi-vendor merge.
     */
    optionEstimateKeys?: string[];
    /** vendor option key → label, the default customer-facing option titles (multi-option mode). */
    optionLabels?: Record<string, string>;
    /**
     * Alias for {@link optionEstimateKeys}, accepted from the "Create Customer Estimate with Options"
     * picker (multi-option-select-modal). Same meaning: 2+ vendor option keys of one on-site approval.
     */
    optionKeys?: string[];
    /** Alias for {@link optionLabels} (vendor option key → customer-facing title), from the options picker. */
    optionTitles?: Record<string, string>;
    /**
     * View mode only: the saved customer estimate has this many options (2+). The dialog loads only its main row
     * (option 1), so it says "Option 1 of N" instead of looking like the whole estimate.
     */
    viewOptionCount?: number;
    /**
     * Edit mode only: this is a change order, the customer sent the estimate back. `customerEstimateKey` is the
     * estimate the host expects to edit; if the job's live customer estimate is another one, nothing is editable.
     */
    changeOrder?: boolean;
    customerEstimateKey?: string;
  }): void {
    const mode = opts.mode ?? 'create';
    const multiKeys = mode === 'create' ? (opts.vendorEstimateKeys ?? []).filter(Boolean) : [];
    const optionKeys =
      mode === 'create' && multiKeys.length < 2
        ? (opts.optionEstimateKeys ?? opts.optionKeys ?? []).filter(Boolean)
        : [];
    this.jobKey.set(opts.jobKey);
    this.vendorEstimateKey.set(opts.vendorEstimateKey ?? '');
    this.vendorEstimateKeys.set(multiKeys.length >= 2 ? multiKeys : []);
    this.vendorNamesByKey.set(opts.vendorNames ?? {});
    this.customerKey.set(opts.customerKey ?? '');
    this.vendorName.set(opts.vendorName ?? '');
    this.estimateNo.set(opts.estimateNo ?? '');
    this.customerEmail.set(opts.customerEmail ?? null);
    this.isEmergency.set(opts.isEmergency ?? false);
    this.viewOptionCount.set(mode === 'view' ? (opts.viewOptionCount ?? 0) : 0);
    this.optionEstimateKeys.set(optionKeys.length >= 2 ? optionKeys : []);
    this.optionLabels = opts.optionLabels ?? opts.optionTitles ?? {};
    this.optionTabs.set([]);
    this.activeOptionIndex.set(0);
    this.optionsSaved.set(false);
    this.savedOptionsView.set(false);
    this.mode.set(mode);
    this.changeOrder.set(mode === 'edit' && !!opts.changeOrder);
    this.expectedCustomerEstimateKey = this.changeOrder() ? (opts.customerEstimateKey ?? '') : '';

    this.isVisible.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.adjustmentProposal.set(null);
    this.markupExplanation.set('');
    this.escalationProposal.set(null);
    this.escalationOpen.set(false);
    this.escalationReason.set('');
    this.escalationSending.set(false);
    this.escalationError.set('');
    this.escalationSent.set(false);
    this.pendingDecisionMode.set(null);
    this.awaitingManualMarkupFix.set(false);
    this.acceptedProposal.set(null);
    this.tripAdjustmentNote.set('');
    this.customerEstimateKey.set(null);
    this.isPersisted.set(false);
    this.estimateTitle.set('');
    this.previousEstimate.set(null);
    this.priorUnpaidCustomerDeposit.set(0);
    this.customerDepositApprovalPending.set(false);
    this.activeTab.set('current');
    this.rows.set([]);
    this.extras.set([]);
    this.portionTitleOverrides.set(new Map());
    this.portionTitleRequired.set(false);
    this.adminOn.set(true);
    this.adminMarkupPercent.set(0);
    this.adminMarkupLineItemKey.set(null);
    this.deletedLineItemKeys = [];
    this.minMarkupPolicy.set(null);

    this.loadMinMarkupPolicy();

    // Create builds a fresh preview from the vendor estimate(s); edit and view both load
    // the existing customer estimate (edit stays editable, view is read-only).
    if (mode === 'create') {
      this.loadPreview();
      this.checkPriorUnpaidDeposit();
    } else {
      this.loadExisting();
    }
  }

  /**
   * Create mode only: looks up whether the job's current (about-to-be-superseded) customer estimate
   * has an unpaid deposit set, so the admin can be warned before that decision is silently dropped —
   * see {@link priorUnpaidCustomerDeposit}. Best-effort: any failure (no prior estimate, network
   * error) just leaves the notice hidden rather than blocking estimate creation.
   */
  private checkPriorUnpaidDeposit(): void {
    this.assignVendorSvc.getCustomerEstimateHistory(this.jobKey()).subscribe({
      next: (historyRes) => {
        const priorKey = historyRes.status ? historyRes.data?.current?.customerEstimateKey : null;
        if (!priorKey) return;

        this.depositSvc.getDepositContextForCustomerEstimate(priorKey).subscribe({
          next: (depositRes) => {
            if (depositRes.status && depositRes.data?.customerDepositUnpaid) {
              this.priorUnpaidCustomerDeposit.set(depositRes.data.customerDepositAmount);
            }
          },
          error: () => {}, // best-effort — no notice on failure
        });
      },
      error: () => {}, // best-effort — no notice on failure
    });
  }

  /**
   * Refreshes {@link customerDepositApprovalPending} for the estimate now on screen. Best-effort: a
   * lookup failure leaves the previous value in place rather than resetting to false, since silently
   * clearing a real pending-approval block on a transient network error would let Save & Send /
   * Resend through when they shouldn't be.
   */
  private refreshDepositApprovalStatus(customerEstimateKey: string): void {
    this.depositSvc.getDepositContextForCustomerEstimate(customerEstimateKey).subscribe({
      next: (res) => {
        if (res.status && res.data) {
          this.customerDepositApprovalPending.set(res.data.customerDepositApprovalPending);
        }
      },
      error: () => {}, // best-effort — leave the prior value in place
    });
  }

  /**
   * Opens the in-app modal that warns the admin about deleting a line item and
   * asks for confirmation. The actual removal happens in confirmDelete() once
   * the admin confirms; cancelDelete() dismisses without changes.
   */
  requestDelete(index: number, isExtra: boolean): void {
    this.pendingDelete.set({ index, isExtra });
  }

  /** Confirm the pending deletion and remove the targeted line. */
  confirmDelete(): void {
    const pending = this.pendingDelete();
    if (!pending) return;
    if (pending.isExtra) {
      this.deleteExtra(pending.index);
    } else {
      this.deleteRow(pending.index);
    }
    this.pendingDelete.set(null);
  }

  /** Dismiss the delete-confirmation modal without removing anything. */
  cancelDelete(): void {
    this.pendingDelete.set(null);
  }

  /**
   * Loads the customer's dynamic minimum-markup policy so the save gate and the reference
   * popover reflect live values. Best-effort: on any failure (no customerKey, network, 404)
   * the policy stays null and the gate falls back to the legacy 50/40 defaults.
   */
  private loadMinMarkupPolicy(): void {
    const customerKey = this.customerKey();
    if (!customerKey) {
      return;
    }
    this.dynMinMarkupSvc.getPolicy(customerKey).subscribe({
      next: (policy) => this.minMarkupPolicy.set(policy),
      error: () => this.minMarkupPolicy.set(null),
    });
  }

  close(): void {
    const wasVisible = this.isVisible();
    this.isVisible.set(false);
    if (wasVisible) this.closed.emit();
  }

  /** Path 2: "Save As Invoice" is available only for an already-saved estimate (edit/view mode). */
  readonly canSaveAsInvoice = computed(
    () => this.mode() !== 'create' && this.isPersisted() && !!this.customerEstimateKey(),
  );

  /** Emit the Save-As-Invoice request to the host and close this editor. */
  requestSaveAsInvoice(): void {
    const key = this.customerEstimateKey();
    if (!key) return;
    this.saveAsInvoiceRequested.emit({ customerEstimateKey: key });
    this.isVisible.set(false);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  /**
   * Per-line markup %, compared amount-to-amount (not rate-to-rate) — a rate-only comparison
   * would miss any change to qty/hours entirely (e.g. accepting the minimum-markup proposal's
   * hours bump changes customerAmount but never customerRate).
   */
  actualMarkup(r: CeRow): number {
    const vendorAmount = r.vendorRate * r.vendorQty;
    return vendorAmount > 0 ? (r.customerRate * r.customerQty - vendorAmount) / vendorAmount * 100 : 0;
  }

  /**
   * Whether a line was priced from a vendor-estimate line. A charge the admin added on the customer estimate has no
   * vendor counterpart, so its grey "Vendor" reference row (and a markup % against a $0 cost) would be noise.
   */
  hasVendorSource(r: CeRow): boolean {
    return !!r.vendorEstimateDetailKey || r.vendorAmount > 0 || r.vendorQty > 0;
  }

  // ── Load ──────────────────────────────────────────────────────

  /**
   * CREATE mode: show a non-persisted preview of what the customer estimate would be. Nothing is written
   * until the admin saves.
   *
   * This used to fall back to createCustomerEstimate — which PERSISTS — whenever the preview endpoint
   * answered badly, from the days before that endpoint existed. All three preview endpoints are live now
   * (AdminOnSiteApprovalController: preview-customer-estimate/{key}, -multi-vendor, -multi-option), and the
   * fallback meant any blip persisted an estimate the admin had not asked for: they then pressed Cancel and
   * the row stayed (Nahid, 21 Oct — "if I give cancel, it's still creating… I have created at least three
   * estimates", none of them saved by her). Opening this modal now writes nothing, whatever happens.
   */
  private loadPreview(): void {
    this.isLoading.set(true);

    if (this.isMultiOption()) {
      this.loadOptionsPreview();
      return;
    }

    if (this.isMultiVendor()) {
      this.assignVendorSvc
        .previewMultiVendorCustomerEstimate(this.vendorEstimateKeys())
        .subscribe({
          next: (res) => {
            if (res.status && res.data) {
              this.isLoading.set(false);
              this.applyResponse(res.data, false);
              // Advisory on preview — nothing persisted either way, but surface it up front.
              this.noteProposal(res.data.adjustmentProposal ?? null);
            } else {
              this.previewFailed(res.message);
            }
          },
          error: () => this.previewFailed(),
        });
      return;
    }

    this.assignVendorSvc
      .previewCustomerEstimate(this.vendorEstimateKey())
      .subscribe({
        next: (res) => {
          if (res.status && res.data) {
            this.isLoading.set(false);
            this.applyResponse(res.data, false); // preview only — not yet persisted
            // Advisory on preview — nothing persisted either way, but surface it up front.
            this.noteProposal(res.data.adjustmentProposal ?? null);
          } else {
            this.previewFailed(res.message);
          }
        },
        error: () => this.previewFailed(),
      });
  }

  /**
   * The preview could not be built. Says so and leaves the grid empty — it must never reach for the
   * persisting create endpoint to fill the screen, which is what left unasked-for estimates behind
   * (see {@link loadPreview}). Save stays disabled while there is nothing to save.
   */
  private previewFailed(message?: string): void {
    this.isLoading.set(false);
    this.rows.set([]);
    this.errorMessage.set(
      message?.trim() ||
        'Could not build the customer estimate preview. Nothing was created — close this and try again.',
    );
  }

  /**
   * VIEW/EDIT mode: load the job's customer-estimate history — the current live estimate plus, if it
   * superseded one, a read-only summary of the immediately-previous archived version (rendered as a
   * second tab). The current estimate populates the editable grid via applyResponse.
   */
  private loadExisting(): void {
    this.isLoading.set(true);
    const expected = this.changeOrder() ? this.expectedCustomerEstimateKey.trim().toLowerCase() : '';
    this.assignVendorSvc
      .getCustomerEstimateHistory(this.jobKey(), this.expectedCustomerEstimateKey.trim() || this.customerEstimateKey() || undefined)
      .subscribe({
        next: (res) => {
          if (res.status && res.data) {
            this.previousEstimate.set(res.data.previous);
            if (res.data.current) {
              const loaded = (res.data.current.customerEstimateKey ?? '').trim().toLowerCase();
              if (expected && loaded && loaded !== expected) {
                // The job's current estimate is a different row (e.g. option 1 while the customer sent back
                // option 2) — load the one the customer sent back by its own key.
                this.loadChangeOrderByKey(expected);
              } else {
                this.isLoading.set(false);
                this.applyResponse(res.data.current, true);
                // A saved estimate with options: show every option, not just the main row (option 1).
                if (this.mode() === 'view') this.loadSavedOptions();
              }
            } else if (expected) {
              this.loadChangeOrderByKey(expected);
            } else {
              this.isLoading.set(false);
              this.errorMessage.set('No customer estimate found for this job.');
            }
          } else if (expected) {
            this.loadChangeOrderByKey(expected);
          } else {
            this.isLoading.set(false);
            this.errorMessage.set(res.message || 'Failed to load customer estimate.');
          }
        },
        error: () => {
          if (expected) {
            this.loadChangeOrderByKey(expected);
            return;
          }
          this.isLoading.set(false);
          this.errorMessage.set('Failed to load customer estimate.');
        },
      });
  }

  /**
   * View mode: loads every option of the job's saved customer estimate (V1 PreviewEstimates shows them all) into the
   * option tabs, read-only, each with its title ("(APPROVED)" once the customer picked it), lines, admin fee and total.
   * A single estimate (one option back) keeps the plain view. Best-effort: on failure the main row stays on screen.
   */
  private loadSavedOptions(): void {
    this.assignVendorSvc.getCustomerEstimateOptions(this.jobKey(), this.customerEstimateKey() || undefined).subscribe({
      next: (res) => {
        const options = res.status ? (res.data?.options ?? []) : [];
        if (this.mode() !== 'view' || options.length < 2) return;
        const tabs: CeOptionTab[] = options.map((o) => ({
          vendorEstimateKey: o.vendorEstimateKey || o.customerEstimateKey,
          vendorLabel: o.title,
          title: o.approvedByCustomer ? `${o.title} (APPROVED)` : o.title,
          ...this.parseResponseLines(o.estimate),
          adjustmentProposal: null,
          acceptedProposal: null,
          awaitingManualMarkupFix: false,
          customerEstimateKey: o.customerEstimateKey,
        }));
        this.optionEstimateKeys.set(tabs.map((t) => t.vendorEstimateKey));
        this.optionTabs.set(tabs);
        this.savedOptionsView.set(true);
        this.viewOptionCount.set(tabs.length);
        this.loadOption(0);
      },
      error: () => {}, // best-effort — option 1 stays on screen with its "Option 1 of N" note
    });
  }

  /**
   * Change order only: loads the exact customer estimate the customer sent back by its key, for when the job's
   * current estimate (history) is a different row — the picked option of a multi-option estimate. Never edits or
   * re-sends anything but that key: if the lookup fails or returns another key, the error banner is shown.
   */
  private loadChangeOrderByKey(expectedLower: string): void {
    const notTheOne =
      'The customer estimate on file for this job is not the one the customer sent back. Open it from the Estimates page.';
    this.assignVendorSvc.getCustomerEstimateByKey(this.expectedCustomerEstimateKey.trim()).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        const data = res.status ? res.data : null;
        if (data && (data.customerEstimateKey ?? '').trim().toLowerCase() === expectedLower) {
          // The history's "previous" version belongs to the main row, not this option.
          this.previousEstimate.set(null);
          this.applyResponse(data, true);
        } else {
          this.errorMessage.set(notTheOne);
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set(notTheOne);
      },
    });
  }

  private applyResponse(data: CreateCustomerEstimateResponse, persisted: boolean): void {
    this.isPersisted.set(persisted);
    this.customerEstimateKey.set(data.customerEstimateKey);

    // Only a persisted estimate has a meaningful key/approval state to check — a non-persisted
    // preview's customerEstimateKey is empty, and nothing can be sent from it anyway.
    if (persisted && data.customerEstimateKey) {
      this.refreshDepositApprovalStatus(data.customerEstimateKey);
    } else {
      this.customerDepositApprovalPending.set(false);
    }

    const parsed = this.parseResponseLines(data);
    this.adminMarkupLineItemKey.set(parsed.adminMarkupLineItemKey);
    this.rows.set(parsed.rows);
    this.extras.set(parsed.extras);
    this.adminMarkupPercent.set(parsed.adminMarkupPercent);
    this.adminOn.set(parsed.adminOn);
  }

  /**
   * Splits a preview / create / history response's line items into grid rows, added charges and the admin fee. The
   * fee's % and whether it applies come from the response's `adminMarkupPercent` / `adminFeeApplied` (the customer
   * profile's AdminMarkup, or what the saved estimate was charged); an "Admin Fee" row inside lineItems is still
   * honoured for older responses. Shared by the single flow and each option of a multi-option estimate.
   */
  private parseResponseLines(
    data: { lineItems?: CustomerEstimateLineItem[]; adminMarkupLineItemKey?: string } & CustomerEstimateAdminFee,
  ): {
    rows: CeRow[];
    extras: CeRow[];
    adminMarkupPercent: number;
    adminOn: boolean;
    adminMarkupLineItemKey: string | null;
  } {
    const rows: CeRow[] = [];
    const extras: CeRow[] = [];
    let adminPct = 0;
    let adminKey: string | null = data.adminMarkupLineItemKey ?? null;

    for (const li of data.lineItems ?? []) {
      // The backend appends the synthetic "Admin Fee" row into lineItems; pull
      // its percent out for the admin block rather than showing it as a line.
      if (li.chargeTypeKey === 'ADMIN_MARKUP' || /admin\s*fee/i.test(li.chargeType)) {
        adminPct = li.profileMarkupPercent ?? li.calculatedMarkupPercent ?? 0;
        if (li.customerEstimateDetailKey) adminKey = li.customerEstimateDetailKey;
        continue;
      }
      const row: CeRow = {
        chargeType: li.chargeType,
        chargeTypeKey: li.chargeTypeKey,
        description: li.description,
        vendorRate: li.vendorRate,
        vendorQty: li.vendorQty,
        vendorAmount: li.vendorAmount,
        customerRate: li.customerRate,
        customerQty: li.customerQty,
        profileMarkupPercent: li.profileMarkupPercent,
        lineType: li.lineType,
        costIncurred: li.costIncurred,
        customerEstimateDetailKey: li.customerEstimateDetailKey,
        // The backend stores labor keys in the same VendorEstimateDetailKey column, so prefer it and
        // fall back to the labor key. Echoed back on save to preserve the vendor linkage.
        vendorEstimateDetailKey: li.vendorEstimateDetailKey ?? li.vendorEstimateLaborKey,
        isCustomLineItem: !!li.isCustomLineItem,
        sourceVendorEstimateKey: li.sourceVendorEstimateKey,
        sourcePortionTitle: li.sourcePortionTitle,
      };
      if (li.isCustomLineItem) extras.push(row);
      else rows.push(row);
    }

    if (data.adminMarkupPercent != null) adminPct = data.adminMarkupPercent;
    const adminOn = adminPct > 0 && (data.adminFeeApplied ?? true);

    return { rows, extras, adminMarkupPercent: adminPct, adminOn, adminMarkupLineItemKey: adminKey };
  }

  // ── Multi-option mode ─────────────────────────────────────────

  /** The active option tab (multi-option mode), from its stored copy; the live values are in the grid signals. */
  readonly activeOptionTab = computed<CeOptionTab | null>(() => this.optionTabs()[this.activeOptionIndex()] ?? null);

  /** The customer-facing title of the option on screen (live, from the tab's stored copy). */
  readonly activeOptionTitle = computed(() => this.activeOptionTab()?.title ?? '');

  /** The vendor option label of the option on screen. */
  readonly activeOptionVendorLabel = computed(() => this.activeOptionTab()?.vendorLabel ?? '');

  /**
   * One entry per tab: its title, customer total and whether it still waits for a minimum-markup decision. The
   * active tab's numbers come from the live grid; the others from their stored copy.
   */
  readonly optionTabSummaries = computed<CeOptionTabSummary[]>(() => {
    const active = this.activeOptionIndex();
    return this.optionTabs().map((t, index) => {
      const live = index === active;
      const rows = live ? this.rows() : t.rows;
      const extras = live ? this.extras() : t.extras;
      const adminOn = live ? this.adminOn() : t.adminOn;
      const adminPct = live ? this.adminMarkupPercent() : t.adminMarkupPercent;
      const subtotal = rowsSubtotal(rows) + rowsSubtotal(extras);
      return {
        index,
        vendorEstimateKey: t.vendorEstimateKey,
        title: t.title,
        vendorLabel: t.vendorLabel,
        customerTotal: subtotal + (adminOn ? (subtotal * adminPct) / 100 : 0),
        needsDecision: live ? !!this.adjustmentProposal() : !!t.adjustmentProposal,
      };
    });
  });

  /** Multi-option preview: one POST for every option, then a tab per option, in the order the server returns. */
  private loadOptionsPreview(): void {
    this.assignVendorSvc.previewCustomerEstimateMultiOption(this.optionEstimateKeys()).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        const options = res.status ? res.data?.options : null;
        if (!options || options.length < 2) {
          this.errorMessage.set(res.message || 'Failed to build the customer estimate preview.');
          return;
        }
        const tabs: CeOptionTab[] = options.map((o, i) => {
          const parsed = this.parseResponseLines(o.preview ?? { lineItems: [] });
          const vendorLabel = this.optionLabels[o.vendorEstimateKey]?.trim() || o.title?.trim() || `Option ${i + 1}`;
          return {
            vendorEstimateKey: o.vendorEstimateKey,
            vendorLabel,
            title: o.title?.trim() || vendorLabel,
            ...parsed,
            // Advisory on preview, like the single flow: nothing is persisted either way, but surface it up front.
            adjustmentProposal: o.preview?.adjustmentProposal ?? null,
            acceptedProposal: null,
            awaitingManualMarkupFix: false,
            customerEstimateKey: null,
          };
        });
        this.optionTabs.set(tabs);
        this.loadOption(0);
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Failed to build the customer estimate preview.');
      },
    });
  }

  /** Copies the active tab's live grid state into its stored copy (before switching tabs or building a request). */
  private snapshotActiveOption(): void {
    const index = this.activeOptionIndex();
    if (!this.optionTabs()[index]) return;
    this.optionTabs.update((tabs) =>
      tabs.map((t, i) =>
        i === index
          ? {
              ...t,
              rows: this.rows(),
              extras: this.extras(),
              adminOn: this.adminOn(),
              adminMarkupPercent: this.adminMarkupPercent(),
              adminMarkupLineItemKey: this.adminMarkupLineItemKey(),
              adjustmentProposal: this.adjustmentProposal(),
              acceptedProposal: this.acceptedProposal(),
              awaitingManualMarkupFix: this.awaitingManualMarkupFix(),
            }
          : t,
      ),
    );
  }

  /** Puts a tab's stored copy into the grid signals and makes it the active tab. */
  private loadOption(index: number): void {
    const t = this.optionTabs()[index];
    if (!t) return;
    this.activeOptionIndex.set(index);
    this.rows.set(t.rows);
    this.extras.set(t.extras);
    this.adminOn.set(t.adminOn);
    this.adminMarkupPercent.set(t.adminMarkupPercent);
    this.adminMarkupLineItemKey.set(t.adminMarkupLineItemKey);
    this.adjustmentProposal.set(t.adjustmentProposal);
    this.acceptedProposal.set(t.acceptedProposal);
    this.awaitingManualMarkupFix.set(t.awaitingManualMarkupFix);
    // Grid-local state that belongs to the tab the admin was on.
    this.tripAdjustmentNote.set('');
    this.pendingDelete.set(null);
    this.deletedLineItemKeys = [];
  }

  /** The admin switches to another option's tab. */
  selectOption(index: number): void {
    if (index === this.activeOptionIndex() || !this.optionTabs()[index]) return;
    this.snapshotActiveOption();
    this.loadOption(index);
  }

  /** The admin edits an option's customer-facing title. */
  setOptionTitle(index: number, title: string): void {
    this.optionTabs.update((tabs) => tabs.map((t, i) => (i === index ? { ...t, title } : t)));
  }

  /** One option's request, built the way the single create builds its request (see persist()). */
  private buildOptionRequest(t: CeOptionTab): CreateCustomerEstimateOptionRequest {
    const lineItems = this.buildLineItems({
      rows: t.rows,
      extras: t.extras,
      adminOn: t.adminOn,
      adminMarkupPercent: t.adminMarkupPercent,
      adminMarkupLineItemKey: t.adminMarkupLineItemKey,
      deletedKeys: [],
    });
    const request: CreateCustomerEstimateOptionRequest = {
      vendorEstimateKey: t.vendorEstimateKey,
      title: t.title.trim() || t.vendorLabel,
      lineItems,
      adminFeeRemoved: !t.adminOn && t.adminMarkupPercent > 0,
    };
    if (t.acceptedProposal) {
      // Accepted proposal already applied to the grid: the server applies its own known-accepted numbers.
      request.markupAdjustmentDecision = MarkupAdjustmentDecision.AcceptProposed;
    } else if (t.awaitingManualMarkupFix) {
      // The admin fixed the markup by hand: this option's current lines are the override.
      request.markupAdjustmentDecision = MarkupAdjustmentDecision.ManualOverride;
      request.markupOverrideLineItems = lineItems;
    }
    return request;
  }

  /**
   * Save / Save & Send in multi-option mode: ONE create-customer-estimate-multi-option call for every option.
   * If any option is under the minimum markup and has no decision yet the server persists NOTHING and returns
   * those options' proposals: they are put on their tabs (marked as needing a decision) and the admin decides,
   * then saves again. On success the MAIN key is announced like a single create (customerEstimateCreated), with
   * the SMALLEST option customer total (the deposit basis), so the host's deposit and send steps run unchanged.
   */
  private saveMultiOption(mode: 'save' | 'mail'): void {
    this.snapshotActiveOption();
    const tabs = this.optionTabs();
    if (tabs.length < 2) return;

    // A proposal is still waiting for the admin on some tab: show it instead of sending an undecided option.
    const undecided = tabs.findIndex((t) => !!t.adjustmentProposal);
    if (undecided >= 0) {
      this.errorMessage.set(`Decide on the minimum-markup fix for ${tabs[undecided].title.trim() || tabs[undecided].vendorLabel} first.`);
      this.loadOption(undecided);
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    this.assignVendorSvc.createCustomerEstimateMultiOption({ options: tabs.map((t) => this.buildOptionRequest(t)) }).subscribe({
      next: (res) => {
        if (!res.status || !res.data) {
          this.isSaving.set(false);
          this.errorMessage.set(res.message || 'Failed to create customer estimate.');
          return;
        }
        const results = res.data.options ?? [];
        const withProposal = results.filter((o) => !!o.adjustmentProposal);

        if (withProposal.length > 0 || !res.data.customerEstimateKey) {
          // Under minimum markup, nothing persisted: put each proposal on its option's tab.
          this.isSaving.set(false);
          if (withProposal.length === 0) {
            this.errorMessage.set(res.data.message || res.message || 'Failed to create customer estimate.');
            return;
          }
          this.optionTabs.update((current) =>
            current.map((t) => {
              const proposal = withProposal.find((o) => this.sameKey(o.vendorEstimateKey, t.vendorEstimateKey))?.adjustmentProposal;
              // The decision sent for this option was answered by a new proposal: start over on it, like the single flow.
              return proposal
                ? { ...t, adjustmentProposal: proposal, acceptedProposal: null, awaitingManualMarkupFix: false }
                : t;
            }),
          );
          this.pendingDecisionMode.set(mode);
          const first = this.optionTabs().findIndex((t) => !!t.adjustmentProposal);
          this.loadOption(first >= 0 ? first : this.activeOptionIndex());
          return;
        }

        this.applySavedOptions(res.data.customerEstimateKey, results);
        this.customerEstimateCreated.emit({
          customerEstimateKey: res.data.customerEstimateKey,
          customerTotal: this.smallestOptionTotal(results),
        });

        this.afterSaved(res.data.customerEstimateKey, mode);
      },
      error: () => {
        this.isSaving.set(false);
        this.errorMessage.set('Failed to create customer estimate.');
      },
    });
  }

  /**
   * What a successful save ends in (Nahid, 21 Oct): the send dialog, whichever button started it — "after
   * saving, it should directly take me to this pop-up". The one exception is the deposit gate a plain Save
   * is allowed through but an email is not ({@link save}), where it would open a dialog that cannot send.
   */
  private afterSaved(ceKey: string, mode: 'save' | 'mail'): void {
    if (mode === 'mail' || !this.customerDepositApprovalPending()) {
      this.sendEmail(ceKey);
      return;
    }

    this.isSaving.set(false);
    this.successMessage.set(
      'Customer estimate saved. It cannot be emailed until the SVC-manager deposit approval clears.',
    );
  }

  /** The saved options replace the tabs' contents (keys, saved numbers); the estimate is then read-only. */
  private applySavedOptions(mainKey: string, results: CreateCustomerEstimateOptionResult[]): void {
    const active = this.activeOptionIndex();
    this.optionTabs.update((tabs) =>
      tabs.map((t) => {
        const saved = results.find((o) => this.sameKey(o.vendorEstimateKey, t.vendorEstimateKey));
        if (!saved) return t;
        return {
          ...t,
          ...this.parseResponseLines(saved),
          title: saved.title?.trim() || t.title,
          customerEstimateKey: saved.customerEstimateKey || null,
          adjustmentProposal: null,
          acceptedProposal: null,
          awaitingManualMarkupFix: false,
        };
      }),
    );
    this.customerEstimateKey.set(mainKey);
    this.isPersisted.set(true);
    this.optionsSaved.set(true);
    this.loadOption(active);
    this.refreshDepositApprovalStatus(mainKey);
  }

  /**
   * The smallest option's customer total: the deposit basis for a multi-option estimate (the customer may pick
   * the cheapest one). Falls back to the grid totals when the response carries none.
   */
  private smallestOptionTotal(results: CreateCustomerEstimateOptionResult[]): number {
    const totals = results.map((o) => o.customerTotal).filter((n) => Number.isFinite(n));
    if (totals.length) return Math.min(...totals);
    const fallback = this.optionTabSummaries().map((s) => s.customerTotal);
    return fallback.length ? Math.min(...fallback) : 0;
  }

  private sameKey(a: string | null | undefined, b: string | null | undefined): boolean {
    return !!a && !!b && a.replace(/[{}]/g, '').toLowerCase() === b.replace(/[{}]/g, '').toLowerCase();
  }

  // ── Editing ───────────────────────────────────────────────────

  /**
   * The Trip row's Qty/Rate as they were when the admin started typing in it, captured on focus.
   *
   * The grid binds `(ngModelChange)`, which fires on EVERY KEYSTROKE — typing "190" into a Trip rate
   * arrives as 1, then 19, then 190. Running the trip rule on each of those treats the half-typed
   * numbers as real edits (1 reads as a $94 cut, 19 as a $76 cut) and walks the Labor line up and down
   * on the way to the real answer, leaving the row out of step with the input. V1 only reacts to a
   * committed cell (its ENTER handler), so the adjustment waits for blur and measures from here.
   */
  private tripEditBaseline: { index: number; qty: number; rate: number } | null = null;

  /** A Trip cell took focus: remember what it held before the admin types. */
  beginRowEdit(index: number): void {
    const row = this.rows()[index];
    this.tripEditBaseline =
      row?.lineType === 'trip' ? { index, qty: row.customerQty, rate: row.customerRate } : null;
  }

  /**
   * A Trip cell lost focus: now the typed value is final, so apply the rule once, against what the row
   * held before the edit started.
   */
  commitRowEdit(index: number): void {
    const baseline = this.tripEditBaseline;
    this.tripEditBaseline = null;
    if (!baseline || baseline.index !== index || this.isReadOnly()) return;
    this.applyTripShortfallToLabor(index, baseline.rate * baseline.qty, baseline.qty, baseline.rate);
  }

  editRow(index: number, field: 'description' | 'customerQty' | 'customerRate', value: string): void {
    this.rows.update((rows) => {
      const next = [...rows];
      const r = { ...next[index] };
      if (field === 'description') r.description = value;
      else if (field === 'customerQty') r.customerQty = parseFloat(value) || 0;
      else r.customerRate = parseFloat(value) || 0;
      next[index] = r;
      return next;
    });
  }

  /**
   * V1's trip-charge rule, applied automatically (ProjectRCS `CreateNew.cshtml` trip branches, and the
   * matching copies in the multi-option and multi-vendor forms).
   *
   * The trip charge can NEVER change. Editing a Trip row's Qty or Rate reverts that row to what it was
   * and moves the dollar difference onto the FIRST Labor row instead, as HOURS — the labor rate is read,
   * never written. Hours move in 0.5-hour steps, rounded away from zero so the step always covers the
   * difference, exactly as V1's `for(;;) tempqty += 0.5` loop does.
   *
   * Two deliberate departures from V1, both of which are V1 defects rather than rules:
   *  - V1 takes `Math.abs` of the difference, so LOWERING a trip charge also RAISES labor. Here the
   *    difference is signed: lowering the trip charge lowers labor hours (floored at 0).
   *  - V1 increments before testing, so even a zero-dollar re-entry adds 0.5 h. Here a zero difference
   *    is a no-op.
   *
   * Multi-vendor grids hold every vendor's lines in one array, so the labor row must belong to the SAME
   * vendor as the trip row — otherwise vendor B's trip charge would land on vendor A's labor.
   *
   * The admin fee needs no special handling: it is a computed over the line subtotal, so it re-derives
   * from the adjusted rows automatically.
   */
  private applyTripShortfallToLabor(
    tripRowIndex: number,
    beforeAmount: number,
    originalTripQty: number,
    originalTripRate: number,
  ): void {
    this.tripAdjustmentNote.set('');

    const rows = this.rows();
    const tripRow = rows[tripRowIndex];
    if (!tripRow) return;

    const delta = tripRow.customerRate * tripRow.customerQty - beforeAmount;

    const revertTrip = () =>
      this.rows.update((current) => {
        const next = [...current];
        const row = next[tripRowIndex];
        if (row) next[tripRowIndex] = { ...row, customerQty: originalTripQty, customerRate: originalTripRate };
        return next;
      });

    // Re-entering the same value changes nothing; still revert so a cosmetic edit cannot stick.
    if (Math.abs(delta) < 0.005) {
      revertTrip();
      return;
    }

    // Same vendor only (multi-vendor merges every vendor's lines into one grid).
    const laborRowIndex = rows.findIndex(
      (r) =>
        r.lineType === 'labor' &&
        r.customerRate > 0 &&
        r.sourceVendorEstimateKey === tripRow.sourceVendorEstimateKey,
    );

    if (laborRowIndex === -1) {
      // V1 silently drops the edit here. Revert it the same way, but say so rather than looking broken.
      revertTrip();
      this.tripAdjustmentNote.set(
        'The trip charge cannot be changed on its own, and there is no labor line to move it to, so the edit was undone.',
      );
      return;
    }

    const laborRow = rows[laborRowIndex];
    const exactHours = delta / laborRow.customerRate;
    // Round the MAGNITUDE up to the next 0.5 so the step always covers the difference, in either direction.
    const steppedHours = Math.sign(exactHours) * Math.ceil(Math.abs(exactHours) / 0.5) * 0.5;
    const resultingHours = Math.max(0, laborRow.customerQty + steppedHours);
    const appliedHours = resultingHours - laborRow.customerQty;

    this.rows.update((current) => {
      const next = [...current];
      const trip = next[tripRowIndex];
      if (trip) next[tripRowIndex] = { ...trip, customerQty: originalTripQty, customerRate: originalTripRate };
      const labor = next[laborRowIndex];
      if (labor) next[laborRowIndex] = { ...labor, customerQty: resultingHours };
      return next;
    });

    const direction = appliedHours >= 0 ? 'increased' : 'reduced';
    this.tripAdjustmentNote.set(
      `Trip charge kept at ${this.formatCurrency(originalTripRate * originalTripQty)}; ` +
        `labor ${direction} by ${Math.abs(appliedHours).toFixed(2)} hrs ` +
        `(${laborRow.customerQty.toFixed(2)} → ${resultingHours.toFixed(2)} hrs at ${this.formatCurrency(laborRow.customerRate)}/hr).`,
    );
  }


  deleteRow(index: number): void {
    const removed = this.rows()[index];
    if (removed?.customerEstimateDetailKey) this.deletedLineItemKeys.push(removed.customerEstimateDetailKey);
    this.rows.update((rows) => rows.filter((_, i) => i !== index));
  }

  editExtra(index: number, field: 'description' | 'customerQty' | 'customerRate', value: string): void {
    this.extras.update((extras) => {
      const next = [...extras];
      const r = { ...next[index] };
      if (field === 'description') r.description = value;
      else if (field === 'customerQty') r.customerQty = parseFloat(value) || 0;
      else r.customerRate = parseFloat(value) || 0;
      next[index] = r;
      return next;
    });
  }

  deleteExtra(index: number): void {
    const removed = this.extras()[index];
    if (removed?.customerEstimateDetailKey) this.deletedLineItemKeys.push(removed.customerEstimateDetailKey);
    this.extras.update((extras) => extras.filter((_, i) => i !== index));
  }

  addLine(): void {
    const opt = this.customChargeTypeOptions.find((o) => o.key === this.newChargeKey());
    if (!opt) {
      this.errorMessage.set('Choose a charge type to add.');
      return;
    }
    this.errorMessage.set('');
    this.extras.update((extras) => [
      ...extras,
      {
        chargeType: opt.label,
        chargeTypeKey: opt.key,
        description: this.newCostIncurred() === 0 ? 'Cost Incurred (added line)' : 'Proposed (added line)',
        vendorRate: 0,
        vendorQty: 0,
        vendorAmount: 0,
        customerRate: this.newRate(),
        customerQty: this.newQty(),
        lineType: opt.lineType,
        costIncurred: this.newCostIncurred(),
        isCustomLineItem: true,
      },
    ]);
    this.newChargeKey.set('');
    this.newRate.set(0);
    this.newQty.set(1);
  }

  removeAdminFee(): void {
    this.adminOn.set(false);
  }

  restoreAdminFee(): void {
    this.adminOn.set(true);
  }

  // ── Save + minimum-markup gate ────────────────────────────────

  save(mode: 'save' | 'mail'): void {
    if (this.isReadOnly()) return;
    // Nothing on screen to save — a preview that failed to build ({@link previewFailed}) leaves the grid
    // empty, and saving that would write the empty estimate this bug used to create by itself.
    if (!this.rows().length && !this.isMultiOption()) {
      this.errorMessage.set('There is nothing to save — the estimate has no line items.');
      return;
    }
    // A plain Save is still allowed while a deposit approval is pending — only sending to the
    // customer (mail) must wait for it to clear. Mirrors canResendEmail's gate on the read-only side.
    if (mode === 'mail' && this.customerDepositApprovalPending()) {
      this.errorMessage.set('This estimate has a customer deposit awaiting SVC-manager approval — it cannot be sent to the customer until that approval clears.');
      return;
    }
    // Several options: one call saves them all (see saveMultiOption).
    if (this.isMultiOption()) {
      this.saveMultiOption(mode);
      return;
    }
    // Combined estimate: every vendor's section needs the title the customer sees for it (V1 "Enter Estimate Title").
    const untitled = this.untitledVendorGroups();
    if (!this.isPersisted() && untitled.length > 0) {
      this.portionTitleRequired.set(true);
      this.errorMessage.set(
        `Enter the estimate title for each vendor's section (e.g. Plumbing Portion of Quote): ${untitled.map((g) => g.vendorName).join(', ')}.`,
      );
      return;
    }
    // No client-side threshold pre-check: the server's adjustmentProposal (surfaced via
    // persist()/runUpdate() below) is the single source of truth for whether this is blocked.

    // The admin accepted the server's proposal and it's already reflected in the grid — commit it
    // as AcceptProposed so the server applies its own known-accepted numbers.
    const accepted = this.acceptedProposal();
    if (accepted) {
      this.acceptedProposal.set(null);
      this.persist(mode, MarkupAdjustmentDecision.AcceptProposed, undefined, accepted);
      return;
    }

    // If the admin dismissed a proposal to edit manually, this attempt is their fix —
    // send it as ManualOverride with whatever's currently in the grid, rather than an undecided
    // attempt that would just surface the same proposal again unchanged.
    if (this.awaitingManualMarkupFix()) {
      this.awaitingManualMarkupFix.set(false);
      this.persist(mode, MarkupAdjustmentDecision.ManualOverride, this.buildUpdateLineItems());
      return;
    }
    this.persist(mode);
  }

  /**
   * Admin accepts the server-proposed labor-hours adjustment. This only writes the proposed numbers
   * into the grid — nothing is persisted and no email is sent. Closing the proposal modal reveals
   * the updated hours and total in the grid itself, so no separate confirmation is shown. The admin
   * reviews, then clicks Save or Save & Send themselves, and that save goes as AcceptProposed (see
   * save() above). Deliberately does NOT resume the originally-clicked action: accepting a pricing
   * fix shouldn't silently commit or email an estimate the admin hasn't seen adjusted.
   */
  acceptProposedAdjustment(): void {
    const proposal = this.adjustmentProposal();
    if (!proposal) return;

    this.adjustmentProposal.set(null);
    this.pendingDecisionMode.set(null);
    this.awaitingManualMarkupFix.set(false);

    this.applyAcceptedProposalLocally(proposal);
    this.acceptedProposal.set(proposal);
  }

  /**
   * Admin wants to fix the shortfall themselves instead of accepting the proposal. Closes the
   * banner so they can edit the existing grid; the next Save resends as ManualOverride with the
   * current grid contents (see awaitingManualMarkupFix / save() above).
   */
  dismissProposedAdjustment(): void {
    this.adjustmentProposal.set(null);
    this.awaitingManualMarkupFix.set(true);
  }

  /**
   * The proposed fix is still on offer in the footer, after the dialog was dismissed.
   *
   * V1 applies the markup silently and gives the admin no dialog at all; V2 asks first, which is fine
   * until the dialog is closed — then the proposal was unreachable and the only ways forward were to
   * re-price the grid by hand or escalate. Nahid asked for the apply action to stay available on the
   * form itself "before saving". {@link escalationProposal} already retains the server's last proposal
   * for Send For Approval, so this reuses it rather than asking the server again.
   *
   * Hidden once applied ({@link acceptedProposal} is set) and once the grid meets the minimum on its own.
   */
  readonly canApplyRetainedProposal = computed(
    () =>
      !this.isReadOnly() &&
      this.belowMinMarkup() &&
      !this.adjustmentProposal() &&
      !this.acceptedProposal() &&
      !!this.escalationProposal() &&
      !this.escalationProposal()!.noLaborLineAvailable,
  );

  /**
   * Applies the retained proposal to the grid — the same local write {@link acceptProposedAdjustment}
   * does, so the following Save goes as AcceptProposed and the server applies its own known numbers.
   * Nothing is persisted here.
   */
  applyRetainedProposal(): void {
    if (!this.canApplyRetainedProposal()) return;
    const proposal = this.escalationProposal();
    if (!proposal) return;

    this.awaitingManualMarkupFix.set(false);
    this.applyAcceptedProposalLocally(proposal);
    this.acceptedProposal.set(proposal);
  }

  /**
   * Records a proposal the server just returned: it drives the dialog as before, and is also kept (with its
   * explanation) for the top-of-form banner and Send For Approval once the dialog is dismissed.
   */
  private noteProposal(proposal: DynMinMarkupAdjustmentProposal | null): void {
    this.adjustmentProposal.set(proposal);
    if (!proposal) return;
    this.escalationProposal.set(proposal);
    this.markupExplanation.set(proposal.underlying?.explanationMessage ?? '');
  }

  // ── Send For Approval (manager override) ──────────────────────

  /** Opens the reason prompt for a manager override (V1's "send this estimate for approval"). */
  openEscalation(): void {
    if (!this.canEscalate()) return;
    this.escalationError.set('');
    this.escalationReason.set('');
    this.escalationOpen.set(true);
    // The proposal dialog and this one shouldn't stack.
    this.adjustmentProposal.set(null);
  }

  cancelEscalation(): void {
    this.escalationOpen.set(false);
    this.escalationError.set('');
  }

  /**
   * Sends the estimate for a manager's approval instead of saving it. The server stores this exact save request and
   * replays it if an approver approves — so nothing is persisted here, and the admin can close the dialog after.
   */
  sendForApproval(): void {
    if (!this.canEscalate() || this.escalationSending()) return;

    const proposal = this.escalationProposal();
    if (!proposal) {
      this.escalationError.set('Save once first so the approver can see exactly what is below the minimum.');
      return;
    }
    const reason = this.escalationReason().trim();
    if (!reason) {
      this.escalationError.set('Enter why this estimate should be approved below the minimum markup.');
      return;
    }

    const editing = this.isPersisted() && !!this.customerEstimateKey();
    const lineItems = this.buildUpdateLineItems();
    const request: MarkupEscalationRequest = {
      jobKey: this.jobKey(),
      customerKey: this.customerKey(),
      customerEstimateKey: editing ? this.customerEstimateKey() : null,
      vendorEstimateKey: this.vendorEstimateKey() || null,
      optionKey: proposal.optionKey || this.vendorEstimateKey() || 'merged',
      requestKind: editing ? 'Update' : 'Create',
      reason,
      thresholdResult: proposal.underlying,
      ...(editing
        ? { originalUpdateRequest: { customerEstimateKey: this.customerEstimateKey()!, lineItems } }
        : {
            originalCreateRequest: {
              vendorEstimateKey: this.vendorEstimateKey(),
              ...(this.isMultiVendor()
                ? { vendorEstimateKeys: this.vendorEstimateKeys(), vendorPortionTitles: this.buildVendorPortionTitles() }
                : {}),
              lineItems,
              adminFeeRemoved: !this.adminOn() && this.adminMarkupPercent() > 0,
            },
          }),
    };

    this.escalationSending.set(true);
    this.escalationError.set('');
    this.dynMinMarkupSvc.escalate(request).subscribe({
      next: () => {
        this.escalationSending.set(false);
        this.escalationSent.set(true);
        this.escalationOpen.set(false);
        this.successMessage.set(
          'Sent for approval. The estimate is not saved yet — it is created once a manager approves the exception.',
        );
      },
      error: () => {
        this.escalationSending.set(false);
        this.escalationError.set('Could not send this estimate for approval. Please try again.');
      },
    });
  }

  private buildUpdateLineItems(): UpdateCustomerEstimateLineItemRequest[] {
    const deletedKeys = this.isPersisted() ? [...this.deletedLineItemKeys] : [];
    // Edit mode: a removed admin fee deletes the saved fee row (the update never infers a delete from absence).
    const savedFeeKey = this.adminMarkupLineItemKey();
    if (this.isPersisted() && !this.adminOn() && savedFeeKey && !deletedKeys.includes(savedFeeKey)) {
      deletedKeys.push(savedFeeKey);
    }
    return this.buildLineItems({
      rows: this.rows(),
      extras: this.extras(),
      adminOn: this.adminOn(),
      adminMarkupPercent: this.adminMarkupPercent(),
      adminMarkupLineItemKey: this.adminMarkupLineItemKey(),
      // Edit mode only: rows removed from the grid this session (see deletedLineItemKeys).
      deletedKeys,
    });
  }

  /** The save payload's lines for one grid (the single estimate's, or one option's of a multi-option estimate). */
  private buildLineItems(src: {
    rows: CeRow[];
    extras: CeRow[];
    adminOn: boolean;
    adminMarkupPercent: number;
    adminMarkupLineItemKey: string | null;
    deletedKeys: string[];
  }): UpdateCustomerEstimateLineItemRequest[] {
    const items: UpdateCustomerEstimateLineItemRequest[] = [];
    let tempIndex = 0;
    // The admin fee, recomputed from the current subtotal (same arithmetic as adminFee()).
    const adminFee = src.adminOn ? ((rowsSubtotal(src.rows) + rowsSubtotal(src.extras)) * src.adminMarkupPercent) / 100 : 0;

    for (const r of [...src.rows, ...src.extras]) {
      items.push({
        lineItemKey: r.customerEstimateDetailKey ?? '',
        chargeType: r.chargeType,
        chargeTypeKey: r.chargeTypeKey,
        description: r.description,
        costIncurred: r.costIncurred,
        customerQty: r.customerQty,
        customerRate: r.customerRate,
        customerAmount: r.customerRate * r.customerQty,
        isNewLineItem: !r.customerEstimateDetailKey,
        tempIndex: tempIndex++,
        // Echo the vendor linkage so a seeded vendor line re-inserted server-side keeps its
        // vendor rate/qty/amount (see CeRow.vendorEstimateDetailKey). Undefined for admin-added lines.
        vendorEstimateDetailKey: r.vendorEstimateDetailKey,
        // Echo the source vendor so a re-saved line keeps its vendor-group label (multi-vendor only).
        sourceVendorEstimateKey: r.sourceVendorEstimateKey,
      });
    }

    // Synthetic Admin Fee row, recomputed from the current subtotal.
    if (src.adminOn && src.adminMarkupPercent > 0) {
      items.push({
        lineItemKey: src.adminMarkupLineItemKey ?? '',
        chargeType: 'Admin Fee',
        chargeTypeKey: 'ADMIN_MARKUP',
        description: `Admin fee ${src.adminMarkupPercent.toFixed(2)}% mark-up`,
        customerQty: 1,
        customerRate: adminFee,
        customerAmount: adminFee,
        isNewLineItem: !src.adminMarkupLineItemKey,
        tempIndex: tempIndex++,
      });
    }

    // Edit mode only: rows removed from the grid this session must be sent back explicitly flagged
    // for deletion — UpdateCustomerEstimateAsync never infers a delete from a row's mere absence (see
    // deletedLineItemKeys' doc comment). Meaningless in create mode (nothing persisted yet to delete).
    for (const key of src.deletedKeys) {
      items.push({
        lineItemKey: key,
        isDeleted: true,
        customerQty: 0,
        customerRate: 0,
        customerAmount: 0,
        tempIndex: tempIndex++,
      });
    }

    return items;
  }

  private persist(
    mode: 'save' | 'mail',
    markupAdjustmentDecision?: MarkupAdjustmentDecision,
    markupOverrideLineItems?: UpdateCustomerEstimateLineItemRequest[],
    acceptedProposal?: DynMinMarkupAdjustmentProposal,
  ): void {
    // Read-only view mode never writes.
    if (this.isReadOnly()) return;

    // A change order edits the existing estimate only: if it did not load, saving must not create a new one.
    if (this.changeOrder() && (!this.isPersisted() || !this.customerEstimateKey())) {
      this.errorMessage.set('The customer estimate to edit did not load, so nothing was saved.');
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    // If this session is still just a preview (nothing persisted), create the customer estimate now,
    // sending the admin's edited line items WITH the create so they persist in a single atomic call.
    // (Previously this did create-then-update, which recomputed default pricing and then had to correct
    // it — fragile and prone to duplicate rows / lost edits like a typed labor rate.)
    if (!this.isPersisted() || !this.customerEstimateKey()) {
      this.assignVendorSvc
        .createCustomerEstimate({
          vendorEstimateKey: this.vendorEstimateKey(),
          ...(this.isMultiVendor()
            ? { vendorEstimateKeys: this.vendorEstimateKeys(), vendorPortionTitles: this.buildVendorPortionTitles() }
            : {}),
          lineItems: this.buildUpdateLineItems(),
          markupAdjustmentDecision,
          markupOverrideLineItems,
          // The server adds the customer's admin fee on create unless the admin removed it here.
          adminFeeRemoved: !this.adminOn() && this.adminMarkupPercent() > 0,
        })
        .subscribe({
          next: (res) => {
            if (!res.status || !res.data) {
              this.isSaving.set(false);
              this.errorMessage.set(res.message || 'Failed to create customer estimate.');
              return;
            }

            if (res.data.adjustmentProposal) {
              // Under minimum markup, nothing persisted — show the proposal instead.
              this.isSaving.set(false);
              this.noteProposal(res.data.adjustmentProposal);
              this.pendingDecisionMode.set(mode);
              return;
            }

            this.customerEstimateKey.set(res.data.customerEstimateKey);
            this.isPersisted.set(true);
            this.customerEstimateCreated.emit({
              customerEstimateKey: res.data.customerEstimateKey,
              customerTotal: res.data.customerTotal,
            });
            // Re-hydrate rows from the persisted result so keys/computed fields (admin fee, markup%)
            // reflect what was saved; the admin's edits are already baked in by the backend overlay.
            this.applyResponse(res.data, true);

            this.afterSaved(res.data.customerEstimateKey, mode);
          },
          error: () => {
            this.isSaving.set(false);
            this.errorMessage.set('Failed to create customer estimate.');
          },
        });
      return;
    }

    this.runUpdate(this.customerEstimateKey()!, mode, markupAdjustmentDecision, markupOverrideLineItems, acceptedProposal);
  }

  private runUpdate(
    ceKey: string,
    mode: 'save' | 'mail',
    markupAdjustmentDecision?: MarkupAdjustmentDecision,
    markupOverrideLineItems?: UpdateCustomerEstimateLineItemRequest[],
    acceptedProposal?: DynMinMarkupAdjustmentProposal,
  ): void {
    const request: UpdateCustomerEstimateRequest = {
      customerEstimateKey: ceKey,
      lineItems: this.buildUpdateLineItems(),
      markupAdjustmentDecision,
      markupOverrideLineItems,
    };

    this.assignVendorSvc.updateCustomerEstimate(request).subscribe({
      next: (res) => {
        if (!res.status) {
          this.isSaving.set(false);
          this.errorMessage.set(res.message || 'Failed to save customer estimate.');
          return;
        }

        if (res.data?.adjustmentProposal) {
          // Under minimum markup, nothing persisted — show the proposal instead.
          this.isSaving.set(false);
          this.noteProposal(res.data.adjustmentProposal);
          this.pendingDecisionMode.set(mode);
          return;
        }

        // Persist any newly-created keys so a subsequent save updates instead of duplicating.
        this.reconcileCreatedKeys(res.data);
        this.deletedLineItemKeys = [];

        // Change order only: tell the host the estimate was saved (an edit save otherwise reports nothing), so its
        // deposit step runs before the send, exactly as after creating a new customer estimate.
        if (this.changeOrder()) {
          this.customerEstimateCreated.emit({ customerEstimateKey: ceKey, customerTotal: this.grandTotal() });
        }

        // The update response carries only counts/totals, not lineItems — if the server just applied
        // an accepted proposal, patch the local grid with the known-accepted hours/total ourselves,
        // otherwise it keeps showing the pre-adjustment numbers even though the save succeeded.
        if (acceptedProposal) {
          this.applyAcceptedProposalLocally(acceptedProposal);
        }

        this.afterSaved(ceKey, mode);
      },
      error: () => {
        this.isSaving.set(false);
        this.errorMessage.set('Failed to save customer estimate.');
      },
    });
  }

  private reconcileCreatedKeys(data: unknown): void {
    const created = (data as { createdLineItems?: { tempIndex: number; lineItemKey: string }[] })
      ?.createdLineItems;
    if (!created?.length) return;

    // tempIndex ordering matches buildUpdateLineItems(): rows, then extras, then admin fee.
    const rows = this.rows();
    const extras = this.extras();
    for (const c of created) {
      if (c.tempIndex < rows.length) {
        rows[c.tempIndex] = { ...rows[c.tempIndex], customerEstimateDetailKey: c.lineItemKey };
      } else if (c.tempIndex < rows.length + extras.length) {
        const ei = c.tempIndex - rows.length;
        extras[ei] = { ...extras[ei], customerEstimateDetailKey: c.lineItemKey };
      } else {
        this.adminMarkupLineItemKey.set(c.lineItemKey);
      }
    }
    this.rows.set([...rows]);
    this.extras.set([...extras]);
  }

  /**
   * Applies an accepted minimum-markup proposal to the local grid, using the proposal's own
   * known-accepted numbers (computed server-side) rather than re-deriving anything client-side.
   *
   * Called twice in the accept flow: once when the admin accepts (so they can review the adjusted
   * estimate before saving) and again after the resulting save, because the update response carries
   * only counts/totals and no lineItems to re-hydrate from. Assigning resultingHours absolutely
   * rather than adjusting relatively keeps that second call harmless.
   */
  private applyAcceptedProposalLocally(proposal: DynMinMarkupAdjustmentProposal): void {
    const rows = this.rows();
    const next = [...rows];

    for (const adjustment of proposal.materialLineAdjustments) {
      const index = next.findIndex((r) => r.customerEstimateDetailKey === adjustment.lineItemKey);
      if (index === -1) continue;
      const row = next[index];
      const rate = row.customerQty !== 0 ? adjustment.resultingAmount / row.customerQty : row.customerRate;
      next[index] = { ...row, customerRate: rate };
    }

    for (const adjustment of proposal.laborLineAdjustments) {
      const index = next.findIndex((r) => r.customerEstimateDetailKey === adjustment.lineItemKey);
      if (index === -1) continue;
      next[index] = { ...next[index], customerQty: adjustment.resultingHours };
    }

    this.rows.set(next);
  }

  /**
   * The estimate is saved; hand off to the send dialog to choose recipients. Persisting and sending
   * are deliberately separate — this component owns the save, the send modal owns delivery.
   */
  private sendEmail(ceKey: string): void {
    this.isSaving.set(false);
    // Nahid, 21 Oct: no "choose who to send it to" — the recipients are the account manager by default and
    // the dialog is about to open anyway. It says what happened and what is left to do, nothing else.
    this.successMessage.set('Customer estimate saved. Now send the estimate to the customer.');
    this.sendRequested.emit({ customerEstimateKey: ceKey, isResend: false });
  }

  /**
   * The footer's send action once the estimate is persisted, where it reads "Email to Customer" — it only
   * opens the send dialog (Nahid, 21 Oct: after a save there is nothing left to save). Same hand-off a
   * Save &amp; Send makes, so the host treats it as the one post-creation send.
   */
  emailToCustomer(): void {
    const ceKey = this.customerEstimateKey();
    if (!ceKey || !this.isPersisted() || this.customerDepositApprovalPending()) return;

    this.errorMessage.set('');
    this.sendRequested.emit({ customerEstimateKey: ceKey, isResend: false });
  }

  /**
   * True when the estimate on screen has been persisted and so can be emailed again without
   * saving. Drives the read-only "Resend to Customer" action. Blocked while this estimate's own
   * customer deposit is still awaiting SVC-manager approval — the estimate must not reach the
   * customer until that deposit decision has cleared.
   */
  readonly canResendEmail = computed(
    () =>
      this.isPersisted() &&
      !!this.customerEstimateKey() &&
      !this.viewingArchived() &&
      !this.customerDepositApprovalPending(),
  );

  /**
   * Re-emails an already-saved customer estimate. Writes nothing first - a pure send, so no
   * minimum-markup gate applies. Available from view mode, where the estimate is read-only.
   * `isResend` tells the host not to re-run post-creation follow-ups such as the deposit prompt.
   */
  resendToCustomer(): void {
    const ceKey = this.customerEstimateKey();
    if (!ceKey || !this.isPersisted()) return;

    this.errorMessage.set('');
    this.successMessage.set('');
    this.sendRequested.emit({ customerEstimateKey: ceKey, isResend: true });
  }
}
